import {
  FieldPath,
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { cn } from '../../../utils/classUtils';
import { SelectImageOption } from '../ControlledSingleSelectCardInput';
import FieldErrorMessage from '../FieldErrorMessage';

export interface ControlledSingleSelectCardInputV2Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends UseControllerProps<TFieldValues, TName> {
  options: SelectImageOption[];
}

const ControlledSingleSelectCardInputV2 = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  options,
  name,
  shouldUnregister = true,
  ...controllerProps
}: ControlledSingleSelectCardInputV2Props<TFieldValues, TName>) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController<TFieldValues, TName>({
    name,
    ...controllerProps,
    shouldUnregister,
  });

  return (
    <div>
      <div className='flex flex-row flex-wrap'>
        {(options || []).map((option, i) => {
          const isSelected = option.value === value;

          return (
            <button
              key={option.value}
              className='w-full mb-4'
              onClick={() => {
                if (!isSelected) {
                  onChange(option.value);
                }
              }}
              disabled={option.disabled}
              data-testid={option.label}
            >
              <div
                data-testid={`selected-option-${i}`}
                className={cn(
                  'flex flex-col justify-center items-center rounded-lg py-4 px-6 relative gap-y-2 border bg-white border-reskin-regent-400',
                  {
                    'border-reskin-primary-blue bg-reskin-regent-100':
                      isSelected,
                  },
                )}
              >
                <img
                  src={option.image}
                  alt='icon'
                  className='h-6 w-6 object-contain'
                  data-testid='select-image'
                />
                <div className='flex flex-col justify-center items-center'>
                  <p className='font-inter-medium text-base font-medium text-center text-reskin-primary-dark'>
                    {option.label}
                  </p>
                  <p className='font-inter-light font-light text-xs text-center text-reskin-primary-dark'>
                    {option.subLabel}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <FieldErrorMessage message={error?.message} />
    </div>
  );
};

export default ControlledSingleSelectCardInputV2;
